import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
} from '@mui/material';
import {
  Loop as LoopIcon,
} from '@mui/icons-material';
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useGetList,
  useRedirect,
} from 'react-admin';
import { EventInput, EventClickArg, EventChangeArg } from '@fullcalendar/core';
import type { TaskFilters } from '../types';
import { TaskDialog } from '../../../components/TaskDialog';
import { useTenant } from '../../../contexts/TenantContext';

interface TaskCalendarViewProps {
  filters: TaskFilters;
  onClearFilters?: () => void;
}

interface TaskEvent {
  id?: string;
  title: string;
  scheduledDate: string;
  priority: string;
  status: string;
  description?: string;
  estimatedHours?: number;
  assigneeId?: string;
  categoryId?: string;
  boardId?: string;
  customerId?: string;
  customer?: any;
  assignee?: any;
  category?: any;
  board?: any;
  recurrenceFrequency?: string;
  recurrenceEndDate?: string;
}

import { isVirtualTask } from '../../../utils/taskUtils';

// Color functions (matching ServiceCalendar)



// Production-safe function to ensure drag styles are always applied
const ensureDragStyles = () => {
  if (document.getElementById('fullcalendar-drag-styles')) return;

  const style = document.createElement('style');
  style.id = 'fullcalendar-drag-styles';
  style.textContent = `
    .fc-event {
      cursor: grab !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    }
    .fc-event:hover {
      cursor: grab !important;
    }
    .fc-event.fc-event-dragging {
      cursor: grabbing !important;
      opacity: 0.7 !important;
      z-index: 999 !important;
    }
  `;
  document.head.appendChild(style);
};

export const TaskCalendarView: React.FC<TaskCalendarViewProps> = ({ filters }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { currentTenant } = useTenant();

  const [events, setEvents] = useState<EventInput[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const [calendarView, setCalendarView] = useState(() => {
    return localStorage.getItem('taskCalendarViewMode') || 'timeGridWeek';
  });

  // Get tenant settings for calendar business hours
  const {
    data: tenantSettings,
  } = useGetList('crm/tenant-settings', {
    pagination: { page: 1, perPage: 1 },
  });

  const currentTenantSettings = tenantSettings?.[0];

  useEffect(() => {
    ensureDragStyles();
  }, []);

  // Load tasks based on filters
  useEffect(() => {
    if (dateRange) {
      loadTasks(dateRange.start, dateRange.end);
    }
  }, [filters, currentTenant?.code, dateRange]);

  const loadTasks = async (start: Date, end: Date) => {
    try {
      // Build filter
      const taskFilter: any = {
        scheduledDate: 'not-null',
      };

      // Apply filters from parent
      if (filters.search) taskFilter.q = filters.search;
      if (filters.workflowId) taskFilter.workflowId = filters.workflowId;
      if (filters.customerId) taskFilter.customerId = filters.customerId;
      if (filters.zoneId) taskFilter.zoneId = filters.zoneId;
      if (filters.categoryId) taskFilter.categoryId = filters.categoryId;
      if (filters.statusId && filters.statusId.length > 0) {
        taskFilter.statusId = filters.statusId;
      }
      if (filters.assigneeId) taskFilter.assigneeId = filters.assigneeId;
      if (filters.priority && filters.priority.length > 0) {
        taskFilter.priority = filters.priority;
      }

      // Add start and end date to filter
      taskFilter.startDate = start.toISOString();
      taskFilter.endDate = end.toISOString();

      const response = await dataProvider.getList('tasks', {
        filter: taskFilter,
        sort: { field: 'scheduledDate', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
      });

      const calendarEvents = response.data
        .filter((task: TaskEvent) => task.scheduledDate)
        .map((task: TaskEvent) => {
          const startDate = new Date(task.scheduledDate);
          const durationHours = task.estimatedHours || 1;
          const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

          return {
            id: task.id,
            title: task.title,
            start: task.scheduledDate,
            end: endDate.toISOString(),
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            display: 'block',
            extendedProps: {
              ...task,
              taskId: task.id,
              taskType: 'task',
              durationHours: durationHours,
            },
          };
        });

      setEvents(calendarEvents);

    } catch (error) {
      notify('Failed to load tasks', { type: 'error' });
      console.error('Failed to load tasks:', error);
    }
  };

  const handleDateSelect = () => {
    // Open create dialog with pre-filled date
    setCreateDialogOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const task = clickInfo.event.extendedProps;
    redirect('show', 'tasks', task.taskId || task.id);
  };

  const handleEventChange = async (changeInfo: EventChangeArg) => {
    const { event, oldEvent } = changeInfo;
    const taskId = event.id;

    try {
      const durationHours = event.end
        ? (new Date(event.end).getTime() - new Date(event.start!).getTime()) / (60 * 60 * 1000)
        : 1;

      const newScheduledDate = event.start!.toISOString();
      const newUpdatedAt = new Date().toISOString();

      await dataProvider.update('tasks', {
        id: taskId,
        data: {
          scheduledDate: newScheduledDate,
          estimatedHours: durationHours,
          updatedAt: newUpdatedAt,
        },
        previousData: oldEvent.extendedProps,
      });

      // Update local state to reflect changes without refresh and avoid infinite loop
      setEvents((prevEvents) =>
        prevEvents.map((e) => {
          if (e.id === taskId) {
            return {
              ...e,
              start: newScheduledDate,
              end: event.end ? event.end.toISOString() : e.end,
              extendedProps: {
                ...e.extendedProps,
                scheduledDate: newScheduledDate,
                estimatedHours: durationHours,
                updatedAt: newUpdatedAt,
              },
            };
          }
          return e;
        })
      );

      notify('Task rescheduled', { type: 'success' });
    } catch (error) {
      notify('Failed to reschedule task', { type: 'error' });
      changeInfo.revert();
    }
  };

  const handleEventResize = async (resizeInfo: any) => {
    const { event, oldEvent } = resizeInfo;
    const taskId = event.id;
    const durationHours = (new Date(event.end).getTime() - new Date(event.start).getTime()) / (60 * 60 * 1000);
    const newUpdatedAt = new Date().toISOString();

    try {
      await dataProvider.update('tasks', {
        id: taskId,
        data: {
          estimatedHours: durationHours,
          updatedAt: newUpdatedAt,
        },
        previousData: oldEvent.extendedProps,
      });

      // Update local state to reflect changes without refresh and avoid infinite loop
      setEvents((prevEvents) =>
        prevEvents.map((e) => {
          if (e.id === taskId) {
            return {
              ...e,
              end: event.end.toISOString(),
              extendedProps: {
                ...e.extendedProps,
                estimatedHours: durationHours,
                updatedAt: newUpdatedAt,
              },
            };
          }
          return e;
        })
      );

      notify('Task duration updated', { type: 'success' });
    } catch (error) {
      notify('Failed to update task duration', { type: 'error' });
      resizeInfo.revert();
    }
  };

  const handleViewChange = (view: any) => {
    const newView = view.view.type;
    setCalendarView(newView);
    localStorage.setItem('taskCalendarViewMode', newView);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Calendar Info */}
      {events.length === 0 && (
        <Paper sx={{ p: 3, mb: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body1" color="text.secondary">
            No tasks with scheduled dates found. Create tasks with scheduled dates to see them on the calendar.
          </Typography>
        </Paper>
      )}

      <Card sx={{ flex: 1, overflow: 'auto' }}>
        <CardContent
          sx={{
            '& .fc-event': {
              background: 'transparent !important',
              border: 'none !important',
              cursor: 'grab !important',
              '&:hover': {
                cursor: 'grab !important',
              },
              '&.fc-event-dragging': {
                cursor: 'grabbing !important',
                opacity: '0.8 !important',
              },
              '&.fc-event-resizing': {
                opacity: '0.7 !important',
                cursor: 'ns-resize !important',
              },
            },
            '& .fc-event-main': {
              overflow: 'visible !important',
            },
            '& .fc-daygrid-event': {
              background: 'transparent !important',
              border: 'none !important',
            },
            '& .fc-timegrid-event': {
              background: 'transparent !important',
              border: 'none !important',
              '&.fc-event-resizing': {
                cursor: 'ns-resize !important',
              },
            },
            '& .fc-event-resizer': {
              display: 'block !important',
              position: 'absolute !important',
              bottom: '0 !important',
              left: '0 !important',
              right: '0 !important',
              height: '8px !important',
              cursor: 'ns-resize !important',
              background: 'transparent !important',
              '&:hover': {
                background: 'rgba(33, 150, 243, 0.2) !important',
              },
            },
            '& .fc-event-resizer-end': {
              display: 'block !important',
              bottom: '0 !important',
              cursor: 'ns-resize !important',
            },
            '& .fc-event-title, & .fc-event-time': {
              display: 'none !important',
            },
          }}
        >
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            initialView={calendarView}
            viewDidMount={handleViewChange}
            datesSet={(dateInfo) => {
              setDateRange({ start: dateInfo.start, end: dateInfo.end });
            }}
            editable={true}
            selectable={true}
            selectMirror={true}
            droppable={true}
            dayMaxEvents={true}
            weekends={true}
            events={events}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventChange={handleEventChange}
            eventResize={handleEventResize}
            height="auto"
            slotMinTime={currentTenantSettings?.calendarStartTime || "06:00:00"}
            slotMaxTime={currentTenantSettings?.calendarEndTime || "20:00:00"}
            slotDuration="00:15:00"
            snapDuration="00:15:00"
            businessHours={{
              daysOfWeek: [1, 2, 3, 4, 5, 6],
              startTime: currentTenantSettings?.calendarStartTime?.slice(0, 5) || '08:00',
              endTime: currentTenantSettings?.calendarEndTime?.slice(0, 5) || '18:00',
            }}
            eventDisplay="block"
            displayEventTime={true}
            allDaySlot={false}
            eventTextColor="#ffffff"
            eventConstraint={{
              startTime: currentTenantSettings?.calendarStartTime || '06:00:00',
              endTime: currentTenantSettings?.calendarEndTime || '22:00:00',
            }}
            eventOverlap={true}
            eventResizableFromStart={false}
            eventDurationEditable={true}
            eventStartEditable={true}
            eventDragMinDistance={5}
            dragRevertDuration={300}
            eventClassNames={() => {
              return ['task-event'];
            }}
            eventContent={(info) => {
              const task = info.event.extendedProps as any;
              const time = new Date(info.event.start!).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });

              const getPriorityColor = (priority: string) => {
                switch (priority?.toLowerCase()) {
                  case 'urgent':
                  case 'critical':
                    return '#dc2626';
                  case 'high':
                    return '#ef4444';
                  case 'medium':
                    return '#2563eb';
                  case 'low':
                    return '#16a34a';
                  default:
                    return '#6b7280';
                }
              };

              const isMonthView = info.view.type === 'dayGridMonth';

              if (isMonthView) {
                return (
                  <div
                    style={{
                      padding: '2px 6px',
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      height: 'auto',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      minHeight: '22px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.borderColor = 'rgba(33, 150, 243, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <span style={{
                      fontSize: '0.7rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500,
                      flexShrink: 0,
                    }}>{time}</span>
                    {(task.priority === 'urgent' || task.priority === 'high') && (
                      <span style={{
                        backgroundColor: getPriorityColor(task.priority),
                        color: 'white',
                        padding: '0px 3px',
                        borderRadius: '2px',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        flexShrink: 0,
                        lineHeight: 1.2,
                      }}>{task.priority.charAt(0)}</span>
                    )}
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: 'rgba(0, 0, 0, 0.87)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}>{info.event.title}</span>
                    {task.assignee?.name && (
                      <span style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}>{task.assignee.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                );
              }

              return (
                <div
                  style={{
                    padding: '8px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(33, 150, 243, 0.3)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: 'rgba(0, 0, 0, 0.87)',
                    marginBottom: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}>{info.event.title}</div>

                  {task.customer?.displayName && (
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}>{task.customer.displayName}</div>
                  )}

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '4px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontWeight: 500,
                      }}>{time}</span>
                      {isVirtualTask(task) && (
                        <div title="Recurring Task Instance" style={{ display: 'flex', alignItems: 'center' }}>
                          <LoopIcon sx={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                        </div>
                      )}
                    </div>
                    {task.priority && (
                      <span style={{
                        backgroundColor: getPriorityColor(task.priority),
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>{task.priority}</span>
                    )}
                  </div>

                  {!isVirtualTask(task) && task.recurrenceFrequency && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginBottom: '4px',
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                    }}>
                      <LoopIcon sx={{ fontSize: '0.8rem', color: '#1976d2' }} />
                      <span>
                        {task.recurrenceFrequency}
                        {task.recurrenceEndDate && `, Ends ${new Date(task.recurrenceEndDate).toLocaleDateString()}`}
                      </span>
                    </div>
                  )}

                  {task.category?.name && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}>{task.category.name}</div>
                  )}

                  {task.assignee?.name && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '6px',
                    }}>
                      <div style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}>{task.assignee.name.charAt(0).toUpperCase()}</div>
                      <span style={{
                        fontSize: '0.8rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontWeight: 500,
                      }}>{task.assignee.name}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
        </CardContent>
      </Card>

      {/* Edit Task Dialog */}
      {/* Task Dialog (Create & Edit) */}
      <TaskDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
        }}
        onSuccess={() => {
          refresh();
          loadTasks(dateRange!.start, dateRange!.end);
          setCreateDialogOpen(false);
        }}
        onDelete={() => {
          refresh();
          loadTasks(dateRange!.start, dateRange!.end);
        }}
      />
    </Box>
  );
};